import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, Home as HomeIcon, Building2, Hammer, Wrench, Zap, Snowflake, Sun, Cpu, Cable, Wifi } from "lucide-react";

const STEPS = [
  { key: "project", q: "Neubau oder Sanierung?", options: [
    { v: "neubau", label: "Neubau", icon: Hammer },
    { v: "sanierung", label: "Sanierung", icon: Wrench },
  ]},
  { key: "object", q: "Welches Gebäude?", options: [
    { v: "wohnung", label: "Wohnung", icon: HomeIcon },
    { v: "efh", label: "Einfamilienhaus", icon: Building2 },
    { v: "mfh", label: "Mehrfamilienhaus", icon: Building2 },
    { v: "wohnanlage", label: "Wohnanlage", icon: Building2 },
  ]},
  { key: "heating", q: "Heizung erneuern oder neu planen?", options: [
    { v: "yes", label: "Ja", icon: Zap },
    { v: "no", label: "Nein", icon: Zap },
  ]},
  { key: "cooling", q: "Kühlung gewünscht?", options: [
    { v: "yes", label: "Ja", icon: Snowflake },
    { v: "no", label: "Nein", icon: Snowflake },
  ]},
  { key: "pv", q: "Photovoltaik vorhanden oder geplant?", options: [
    { v: "yes", label: "Ja", icon: Sun },
    { v: "no", label: "Nein", icon: Sun },
  ]},
  { key: "automation", q: "Gebäudeautomation gewünscht?", options: [
    { v: "yes", label: "Ja", icon: Cpu },
    { v: "basic", label: "Nur Basis (Licht, Thermostat)", icon: Cpu },
    { v: "no", label: "Nein", icon: Cpu },
  ]},
  { key: "wiring", q: "Kabel möglich oder Funk gewünscht?", options: [
    { v: "cable", label: "Kabel (Bus)", icon: Cable },
    { v: "wireless", label: "Funk", icon: Wifi },
    { v: "mixed", label: "Mischung", icon: Wifi },
  ]},
];

const SYSTEMS = {
  "planung-bim": { label: "Planung & BIM", href: "/planung-bim", d: "Technische Planung und BIM-Koordination aller Gewerke." },
  "waermepumpe": { label: "Wärmepumpe", href: "/energie-heizung", d: "Luft-Wasser-Wärmepumpe für Heizen, Warmwasser und ggf. Kühlen." },
  "fbh": { label: "Fußbodenheizung", href: "/energie-heizung", d: "Niedertemperatur-Flächenheizung — ideal zur Wärmepumpe." },
  "pv": { label: "PV & Speicher", href: "/energie-heizung", d: "Photovoltaik mit Speicher und Eigenverbrauchsoptimierung." },
  "ems": { label: "Energiemanagement", href: "/energie-heizung", d: "Monitoring, Lastmanagement und PV-Überschuss-Steuerung." },
  "automation-bus": { label: "Automation (Bus)", href: "/gebaeudeautomation", d: "Kabelgebundene Gebäudeautomation für Neubauten." },
  "automation-funk": { label: "Automation (Funk)", href: "/gebaeudeautomation", d: "Funk-Automation — ideal für Sanierungen ohne neue Leitungen." },
  "sensorik": { label: "Sensorik", href: "/gebaeudeautomation", d: "Temperatur, CO₂, Präsenz, Verbrauch — Datenbasis für Automation." },
  "zentralsystem": { label: "Wohnungszentrale Verwaltung", href: "/wohnanlagen", d: "Zentrale Technik mit wohnungsweiser Regelung und Erfassung." },
};

function recommend(a) {
  const sys = new Set(["planung-bim"]);
  if (a.heating === "yes") { sys.add("waermepumpe"); if (a.project === "neubau") sys.add("fbh"); }
  if (a.cooling === "yes") sys.add("waermepumpe");
  if (a.pv === "yes") { sys.add("pv"); sys.add("ems"); }
  if (a.automation === "yes" || a.automation === "basic") {
    sys.add("sensorik");
    if (a.wiring === "cable" || a.wiring === "mixed") sys.add("automation-bus");
    if (a.wiring === "wireless" || a.wiring === "mixed") sys.add("automation-funk");
  }
  if (a.object === "mfh" || a.object === "wohnanlage") sys.add("zentralsystem");
  return [...sys];
}

export default function ProductFinder() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const current = STEPS[step];
  const progress = ((step + (done ? 1 : 0)) / STEPS.length) * 100;

  const pick = (v) => {
    const next = { ...answers, [current.key]: v };
    setAnswers(next);
    if (step < STEPS.length - 1) setStep(step + 1);
    else setDone(true);
  };

  const back = () => {
    if (done) { setDone(false); return; }
    if (step > 0) setStep(step - 1);
  };

  const sendToContact = () => {
    const payload = {
      source: "produktfinder",
      answers,
      systems: recommend(answers).map((k) => SYSTEMS[k]?.label).filter(Boolean),
    };
    try { sessionStorage.setItem("eco_prefill", JSON.stringify(payload)); } catch {}
    navigate("/kontakt");
  };

  return (
    <div className="page-shell" data-testid="productfinder-page">
      <section className="site-container page-hero max-w-[1200px]">
        <div className="eyebrow mb-6 text-[#5FA637]">Produktfinder</div>
        <h1 className="page-title max-w-4xl">
          Welches System passt<br/>
          <span className="italic-accent">zu Ihrem Gebäude?</span>
        </h1>
        <p className="copy-lead mt-8 text-base md:text-lg">
          Sieben Fragen — am Ende sehen Sie, welche Systembereiche für Ihr Projekt relevant sind. Keine ungeprüften
          Produktempfehlungen; die finale Auslegung erfordert immer eine Projektprüfung.
        </p>

        {/* Progress */}
        <div className="mt-12 mb-10" data-testid="finder-progress">
          <div className="flex justify-between text-[11px] uppercase tracking-[0.15em] text-white/50 mb-3">
            <span>{done ? "Abgeschlossen" : `Frage ${step + 1} von ${STEPS.length}`}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-1 bg-white/10 w-full overflow-hidden">
            <div className="h-full bg-[#5FA637] transition-all duration-500" style={{ width: `${progress}%` }}/>
          </div>
        </div>

        {!done ? (
          <div className="surface-panel p-7 md:p-10" data-testid="finder-step">
            <h2 className="font-display text-2xl md:text-4xl mb-10 leading-tight">
              {current.q}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {current.options.map((o) => (
                <button
                  key={o.v} onClick={() => pick(o.v)}
                  data-testid={`finder-option-${current.key}-${o.v}`}
                  className={`technical-card p-6 text-left group ${
                    answers[current.key] === o.v ? "border-[#5FA637] bg-[#5FA637]/5" : "border-white/15 hover:border-[#5FA637]/60 hover:bg-white/5"
                  }`}
                >
                  <o.icon size={22} strokeWidth={1.5} className="mb-4 text-[#5FA637] group-hover:scale-110 transition-transform"/>
                  <div className="font-display text-base md:text-lg leading-tight">{o.label}</div>
                </button>
              ))}
            </div>

            {step > 0 && (
              <button onClick={back} className="mt-10 flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-white/60 hover:text-white" data-testid="finder-back">
                <ArrowLeft size={14}/> Zurück
              </button>
            )}
          </div>
        ) : (
          <div className="surface-panel p-7 md:p-10" data-testid="finder-result">
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle2 size={24} className="text-[#5FA637]"/>
              <div className="eyebrow text-[#5FA637]">Ergebnis — Vorschlag</div>
            </div>
            <h2 className="font-display text-3xl md:text-5xl mb-6 leading-tight">
              Folgende Systembereiche<br/><span className="italic-accent">könnten passen.</span>
            </h2>
            <p className="text-white/70 mb-8 max-w-2xl">
              Auf Basis Ihrer Antworten empfehlen wir folgende Themen für eine detaillierte Projektprüfung:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10 mb-10" data-testid="finder-systems">
              {recommend(answers).map((k) => {
                const s = SYSTEMS[k];
                if (!s) return null;
                return (
                  <Link key={k} to={s.href} className="bg-[#0A1F38] p-6 hover:bg-[#132E4A] group">
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-display text-lg md:text-xl">{s.label}</div>
                      <ArrowRight size={16} className="text-[#5FA637] group-hover:translate-x-1 transition-transform"/>
                    </div>
                    <p className="text-sm text-white/60">{s.d}</p>
                  </Link>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-4">
              <button onClick={sendToContact} className="btn-primary" data-testid="finder-to-contact">
                Projektanfrage mit Ergebnis <ArrowRight size={14}/>
              </button>
              <button onClick={() => { setDone(false); setStep(0); setAnswers({}); }} className="btn-ghost" data-testid="finder-reset">
                Nochmal starten
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
